import {getLanguagePrefix, normalizeLanguage} from './language';

export const COUNTRY_STORAGE_KEY = 'camdiab-country';
export const COUNTRY_RETURN_PATH_KEY = 'camdiab-country-return-path';

/**
 * Figma-ordered country/language options.
 * id must be unique even when countryCode repeats (e.g. Belgium languages).
 */
export const COUNTRY_OPTIONS = [
    {id: 'au-en', countryName: 'Australia', languageLabel: 'English', languageCode: 'EN', countryCode: 'AU', flagEmoji: '🇦🇺'},
    {id: 'at-de', countryName: 'Austria', languageLabel: 'Deutsch', languageCode: 'DE', countryCode: 'AT', flagEmoji: '🇦🇹'},
    {id: 'be-nl', countryName: 'Belgium', languageLabel: 'Nederlands', languageCode: 'NL', countryCode: 'BE', flagEmoji: '🇧🇪'},
    {id: 'be-fr', countryName: 'Belgium', languageLabel: 'Français', languageCode: 'FR-FR', countryCode: 'BE', flagEmoji: '🇧🇪'},
    {id: 'be-de', countryName: 'Belgium', languageLabel: 'Deutsch', languageCode: 'DE', countryCode: 'BE', flagEmoji: '🇧🇪'},
    {id: 'ca-en', countryName: 'Canada', languageLabel: 'English', languageCode: 'EN-CA', countryCode: 'CA', flagEmoji: '🇨🇦'},
    {id: 'ca-fr', countryName: 'Canada', languageLabel: 'Français', languageCode: 'FR-CA', countryCode: 'CA', flagEmoji: '🇨🇦'},
    {id: 'cz-cs', countryName: 'Czechia', languageLabel: 'Čeština', languageCode: 'CZ', countryCode: 'CZ', flagEmoji: '🇨🇿'},
    {id: 'dk-da', countryName: 'Denmark', languageLabel: 'Dansk', languageCode: 'DK', countryCode: 'DK', flagEmoji: '🇩🇰'},
    {id: 'fi-fi', countryName: 'Finland', languageLabel: 'Suomi', languageCode: 'FI', countryCode: 'FI', flagEmoji: '🇫🇮'},
    {id: 'fi-sv', countryName: 'Finland', languageLabel: 'Svenska', languageCode: 'SE', countryCode: 'FI', flagEmoji: '🇫🇮'},
    {id: 'fr-fr', countryName: 'France', languageLabel: 'Français', languageCode: 'FR-FR', countryCode: 'FR', flagEmoji: '🇫🇷'},
    {id: 'de-de', countryName: 'Germany', languageLabel: 'Deutsch', languageCode: 'DE', countryCode: 'DE', flagEmoji: '🇩🇪'},
    {id: 'it-it', countryName: 'Italy', languageLabel: 'Italiano', languageCode: 'IT', countryCode: 'IT', flagEmoji: '🇮🇹'},
    {id: 'nl-nl', countryName: 'Netherlands', languageLabel: 'Nederlands', languageCode: 'NL', countryCode: 'NL', flagEmoji: '🇳🇱'},
    {id: 'nz-en', countryName: 'New Zealand', languageLabel: 'English', languageCode: 'EN', countryCode: 'NZ', flagEmoji: '🇳🇿'},
    {id: 'no-no', countryName: 'Norway', languageLabel: 'Norsk', languageCode: 'NO', countryCode: 'NO', flagEmoji: '🇳🇴'},
    {id: 'pl-pl', countryName: 'Poland', languageLabel: 'Polski', languageCode: 'PL', countryCode: 'PL', flagEmoji: '🇵🇱'},
    // {id: 'pt-pt', countryName: 'Portugal', languageLabel: 'Português', languageCode: 'PT', countryCode: 'PT', flagEmoji: '🇵🇹'},
    {id: 'es-es', countryName: 'Spain', languageLabel: 'Español', languageCode: 'ES', countryCode: 'ES', flagEmoji: '🇪🇸'},
    {id: 'se-sv', countryName: 'Sweden', languageLabel: 'Svenska', languageCode: 'SE', countryCode: 'SE', flagEmoji: '🇸🇪'},
    {id: 'ch-de', countryName: 'Switzerland', languageLabel: 'Deutsch', languageCode: 'DE-CH', countryCode: 'CH', flagEmoji: '🇨🇭'},
    {id: 'ch-fr', countryName: 'Switzerland', languageLabel: 'Français', languageCode: 'FR', countryCode: 'CH', flagEmoji: '🇨🇭'},
    {id: 'ch-it', countryName: 'Switzerland', languageLabel: 'Italiano', languageCode: 'IT-CH', countryCode: 'CH', flagEmoji: '🇨🇭'},
    {id: 'gb-en', countryName: 'United Kingdom', languageLabel: 'English', languageCode: 'EN', countryCode: 'GB', flagEmoji: '🇬🇧'},
];

const DEFAULT_COUNTRY_BY_LANGUAGE = {
    EN: 'gb-en',
    'EN-CA': 'ca-en',
    DE: 'de-de',
    'DE-CH': 'ch-de',
    FR: 'ch-fr',
    'FR-CA': 'ca-fr',
    'FR-FR': 'fr-fr',
    NL: 'nl-nl',
    IT: 'it-it',
    'IT-CH': 'ch-it',
    CZ: 'cz-cs',
    FI: 'fi-fi',
    PL: 'pl-pl',
    SE: 'se-sv',
    NO: 'no-no',
    DK: 'dk-da',
    ES: 'es-es',
    // PT: 'pt-pt',
};

export function getCountryOption(id) {
    return COUNTRY_OPTIONS.find((option) => option.id === id) || null;
}

export function getStoredCountryId() {
    try {
        return localStorage.getItem(COUNTRY_STORAGE_KEY) || null;
    } catch (e) {
        return null;
    }
}

export function setStoredCountryId(id) {
    try {
        if (id) {
            localStorage.setItem(COUNTRY_STORAGE_KEY, id);
        }
    } catch (e) {
        // ignore storage failures
    }
}

export function getCountryForLanguage(language) {
    const normalized = normalizeLanguage(language);
    const storedId = getStoredCountryId();
    const stored = storedId ? getCountryOption(storedId) : null;
    if (stored && stored.languageCode === normalized) {
        return stored;
    }
    const fallbackId = DEFAULT_COUNTRY_BY_LANGUAGE[normalized] || 'gb-en';
    return getCountryOption(fallbackId) || COUNTRY_OPTIONS[COUNTRY_OPTIONS.length - 1];
}

export function getNavCountryDisplay(language) {
    const option = getCountryForLanguage(language);
    return {
        flag: option.flagEmoji,
        code: option.countryCode,
        id: option.id,
    };
}

/**
 * CamAPS Liberty is hidden for Canada locales and for France (FR-FR default),
 * but visible for Belgium Français (same FR-FR language, country be-fr).
 */
export function isCamAPSLibertyVisible(language) {
    const normalized = normalizeLanguage(language);
    if (normalized === 'FR-CA' || normalized === 'EN-CA') {
        return false;
    }
    if (normalized === 'FR-FR') {
        return getCountryForLanguage(language)?.id === 'be-fr';
    }
    return true;
}

function isLanguagePagePath(pathname) {
    return /(?:^|\/)language\/?$/.test(pathname || '');
}

export function setCountryReturnPath(pathname) {
    try {
        if (pathname && !isLanguagePagePath(pathname)) {
            sessionStorage.setItem(COUNTRY_RETURN_PATH_KEY, pathname);
        }
    } catch (e) {
        // ignore
    }
}

export function consumeCountryReturnPath() {
    try {
        const path = sessionStorage.getItem(COUNTRY_RETURN_PATH_KEY);
        sessionStorage.removeItem(COUNTRY_RETURN_PATH_KEY);
        return path || null;
    } catch (e) {
        return null;
    }
}

/**
 * Strip locale prefixes and rebuild path for a target language.
 */
export function rewritePathForLanguage(path, languageCode) {
    let nextPath = (path || '/').toLowerCase();
    nextPath = nextPath
        .replace(/^(\/de-ch)/, '')
        .replace(/^(\/de)/, '')
        .replace(/^(\/en-ca)/, '')
        .replace(/^(\/fr-ch)/, '')
        .replace(/^(\/fr-ca)/, '')
        .replace(/^(\/fr)/, '')
        .replace(/^(\/nl)/, '')
        .replace(/^(\/it-ch)/, '')
        .replace(/^(\/it)/, '')
        .replace(/^(\/cz)/, '')
        .replace(/^(\/fi)/, '')
        .replace(/^(\/pl)/, '')
        .replace(/^(\/se)/, '')
        .replace(/^(\/nb)/, '')
        .replace(/^(\/dk)/, '')
        .replace(/^(\/es)/, '')
        .replace(/^(\/pt)/, '');

    if (!nextPath || nextPath === '/') {
        nextPath = '/';
    } else if (!nextPath.startsWith('/')) {
        nextPath = '/' + nextPath;
    }

    // Never stay on the language page after selecting
    if (isLanguagePagePath(nextPath)) {
        nextPath = '/';
    }

    const prefix = getLanguagePrefix(languageCode);
    if (!prefix) {
        return nextPath === '/' ? '/' : nextPath;
    }
    return prefix + (nextPath === '/' ? '/' : nextPath);
}
